import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, HelpTip, LoadError, ModalDialog, Skeleton, useWriteLock, } from '@kanos/ui';
import { Plus, ShieldCheck } from 'lucide-react';
import { createContext, useContext, useId, useState } from 'react';
import { ApiError } from './api.js';
import { DiscardDialog } from './discard-dialog.js';
import { currentPath, startSignIn } from './session.js';
import { problemText } from './texts.js';
import { useDraftChanged } from './unsaved.js';
/**
 * Set by a page that names itself (`AreaPage`): its one card then keeps its title for screen
 * readers only - the page's heading already says it, and the same word twice reads as a stutter.
 */
export const PageNamesCard = createContext(false);
/** A titled glass card of a page; `action` sits next to the title. */
export function Card({ id, title, description, help, tool, action, children, }) {
    const titled = !useContext(PageNamesCard);
    return (_jsxs("section", { "aria-labelledby": id, className: "glass grid content-start gap-4 rounded-lg p-5 sm:p-6", children: [_jsxs("header", { className: "flex flex-wrap items-start justify-between gap-x-6 gap-y-3", children: [_jsxs("div", { className: "grid min-w-0 flex-[1_1_18rem] gap-1", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("h2", { id: id, className: titled ? 'scroll-mt-8 text-xl font-semibold tracking-[-0.02em]' : 'sr-only', children: title }), help && _jsx(HelpTip, { label: title, children: help }), tool && _jsx("span", { className: "-my-1.5 -mr-2.5 ml-auto", children: tool })] }), description && _jsx("p", { className: "max-w-prose text-sm text-muted", children: description })] }), action && _jsx("div", { className: "max-w-full", children: action })] }), children] }));
}
/**
 * A list of a page - a table - without a card around it: the table is the surface (DataTable).
 * The title is for assistive technology, the page's heading and tabs name it for everyone else.
 */
export function ListSection({ id, title, description, children, }) {
    return (_jsxs("section", { "aria-labelledby": id, className: "grid content-start gap-4", children: [_jsx("h2", { id: id, className: "sr-only", children: title }), _jsx("p", { className: "sr-only", children: description }), children] }));
}
export function AddButton({ label, onPress, primary = false, }) {
    return (_jsxs(Button, { variant: primary ? 'primary' : 'secondary', size: "sm", onPress: onPress, children: [_jsx(Plus, { "aria-hidden": true, className: "size-4" }), label] }));
}
/** A query that failed, as `LoadError` and `DataTable` show it. */
export const loadFailureOf = (query) => ({
    message: problemText(query.error),
    onRetry: () => void query.refetch(),
    isRetrying: query.isRefetching,
});
/** Loading and failure of a card's data; the content renders only with data. */
export function QueryContent({ query, skeleton, children, }) {
    if (query.isPending) {
        return (_jsx("div", { "aria-busy": true, className: "grid gap-3 py-1", role: "status", "aria-label": "Wird geladen", children: skeleton ?? (_jsxs(_Fragment, { children: [_jsx(Skeleton, { className: "h-4 w-full max-w-md" }), _jsx(Skeleton, { className: "h-4 w-4/5 max-w-sm" }), _jsx(Skeleton, { className: "h-4 w-3/5 max-w-xs" })] })) }));
    }
    if (query.isError)
        return _jsx(LoadError, { failure: loadFailureOf(query) });
    return children(query.data);
}
/** A failure the person should read, announced when it appears. */
export function FormAlert({ children }) {
    return (_jsx("div", { role: "alert", className: "grid justify-items-start gap-3 rounded-sm bg-danger-bg px-4 py-3 text-sm text-danger-fg", children: children }));
}
/**
 * A failed request in the person's words - and, where the API wants a fresh second factor
 * (step-up, ADR-005), the way to give it. Back from the identity provider, the person repeats
 * the action; nothing was done in between.
 */
export function ProblemAlert({ error }) {
    return (_jsxs(FormAlert, { children: [_jsx("p", { children: problemText(error) }), error instanceof ApiError && error.code === 'auth.step-up-required' && (_jsxs(Button, { variant: "secondary", size: "sm", onPress: () => {
                    startSignIn(currentPath(), 'step-up');
                }, children: [_jsx(ShieldCheck, { "aria-hidden": true, className: "size-4" }), "Mit zweitem Faktor best\u00E4tigen"] }))] }));
}
/**
 * A form in a dialog with what every one of them has: the hint when the form still does not fit
 * the API's schema, the API's answer when saving failed, and Cancel and Save at the foot. With
 * `draft` - the fields' values - closing it with changed input asks first.
 */
export function FormDialog({ title, description, submitLabel, isPending, error, unfit = false, onSubmit, onClose, draft = null, size = 'lg', aside, note, children, }) {
    const changed = useDraftChanged(draft);
    const [asking, setAsking] = useState(false);
    const lock = useWriteLock();
    const formId = useId();
    const leave = () => {
        if (changed)
            setAsking(true);
        else
            onClose();
    };
    return (_jsx(ModalDialog, { title: title, description: description, size: size, ...(aside ? { aside } : {}), isOpen: true, onOpenChange: (open) => {
            if (!open)
                leave();
        }, footer: () => (_jsxs(_Fragment, { children: [note && _jsx("p", { className: "self-center text-sm text-muted sm:mr-auto", children: note }), _jsx(Button, { variant: "quiet", onPress: leave, children: "Abbrechen" }), _jsx(Button, { type: "submit", form: formId, isPending: isPending, writes: true, children: submitLabel })] })), children: () => (_jsxs(_Fragment, { children: [_jsxs("form", { id: formId, onSubmit: (event) => {
                        event.preventDefault();
                        onSubmit();
                    }, className: "grid gap-4", children: [lock && _jsx(LockNote, { reason: lock }), children, unfit && (_jsx(FormAlert, { children: "Die Angaben passen noch nicht zusammen. Bitte pr\u00FCfen Sie die Felder." })), error ? _jsx(ProblemAlert, { error: error }) : null] }), asking && _jsx(DiscardDialog, { onKeep: () => setAsking(false), onDiscard: onClose })] })) }));
}
/**
 * Several cards on a page that names itself (after Corly's „Unternehmen“): each keeps its title,
 * for the page's heading names the page, not the cards. With `columns={2}` short cards stand side
 * by side on wide screens (a card that needs the width wraps itself in `WideCard`).
 */
export function CardGroup({ columns = 1, children }) {
    return (_jsx(PageNamesCard.Provider, { value: false, children: _jsx("div", { className: columns === 2 ? 'grid gap-6 xl:grid-cols-2' : 'grid gap-6', children: children }) }));
}
/** A card of a two-column `CardGroup` that takes the whole row. */
export function WideCard({ children }) {
    return _jsx("div", { className: "grid xl:col-span-2", children: children });
}
/** Why the dialog cannot save now - the firm reads and exports only (ADR-045). */
export function LockNote({ reason }) {
    return (_jsx("p", { role: "note", className: "rounded-sm bg-warning-bg px-3 py-2 text-sm text-warning-fg", children: reason }));
}
//# sourceMappingURL=card.js.map